import { useCallback, useEffect, useRef, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useSendWhatsAppFreeForm (PRD.235)                                 */
/*                                                                    */
/*  Thin client for the `psendFreeFormWhatsApp` Cloud Function.       */
/*  Server enforces the 24-hour customer service window — this hook  */
/*  just relays state + a `checkEligibility` preflight for the UI.   */
/* ------------------------------------------------------------------ */

const FREEFORM_URL = import.meta.env.VITE_WHATSAPP_FREEFORM_URL;

export type FreeFormSourceType =
  | 'running_late'
  | 'directions'
  | 'parking'
  | 'custom'
  | 'appointment_followup';

export type FreeFormBlockReason = 'opted_out' | 'opt_in_required' | 'no_inbound' | 'window_expired';

export type FreeFormStatus = 'idle' | 'checking' | 'sending' | 'success' | 'error';

export interface SendFreeFormInput {
  patientId: string;
  message: string;
  sourceType: FreeFormSourceType;
  appointmentId?: string;
  clientReference?: string;
}

export interface SendFreeFormResult {
  jobId: string;
  deliveryId?: string;
  accepted?: boolean;
  providerMessageId?: string;
  lastInboundAt?: string;
  windowExpiresAt?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface FreeFormEligibilityState {
  loading: boolean;
  /** True when the server says we're inside the 24h window. */
  allowed: boolean | null;
  reason: FreeFormBlockReason | null;
  /** Human-readable explanation when not allowed. */
  message: string | null;
  lastInboundAt: string | null;
  windowExpiresAt: string | null;
}

export interface UseSendWhatsAppFreeFormReturn {
  status: FreeFormStatus;
  /** Error from the last send (not the preflight). */
  error: string | null;
  lastResult: SendFreeFormResult | null;
  /** True when the endpoint URL is configured. */
  enabled: boolean;
  /** Current eligibility from the latest preflight. */
  eligibility: FreeFormEligibilityState;
  /** Re-check window eligibility for a patient (dryRun call). */
  checkEligibility: (patientId: string) => Promise<void>;
  /** Send a free-form message. Returns the result or null on failure. */
  send: (input: SendFreeFormInput) => Promise<SendFreeFormResult | null>;
  /** Reset transient state (does not clear `eligibility`). */
  reset: () => void;
}

const INITIAL_ELIGIBILITY: FreeFormEligibilityState = {
  loading: false,
  allowed: null,
  reason: null,
  message: null,
  lastInboundAt: null,
  windowExpiresAt: null,
};

export function useSendWhatsAppFreeForm(): UseSendWhatsAppFreeFormReturn {
  const [status, setStatus] = useState<FreeFormStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SendFreeFormResult | null>(null);
  const [eligibility, setEligibility] = useState<FreeFormEligibilityState>(INITIAL_ELIGIBILITY);
  // Avoid stale eligibility writes from an in-flight check for a previous patient.
  const eligibilityReqId = useRef(0);

  const enabled = Boolean(FREEFORM_URL);

  const checkEligibility = useCallback(async (patientId: string): Promise<void> => {
    if (!FREEFORM_URL || !patientId) {
      setEligibility(INITIAL_ELIGIBILITY);
      return;
    }
    const reqId = ++eligibilityReqId.current;
    setEligibility({ ...INITIAL_ELIGIBILITY, loading: true });
    setStatus('checking');

    try {
      const token = await getIdToken();
      const res = await fetch(FREEFORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ patientId, dryRun: true }),
      });
      const body: unknown = await res.json().catch(() => null);

      // Bail if a newer check has started.
      if (reqId !== eligibilityReqId.current) return;

      if (res.status === 200) {
        const ok = body as {
          allowed: boolean;
          lastInboundAt: string | null;
          windowExpiresAt: string | null;
        };
        setEligibility({
          loading: false,
          allowed: true,
          reason: null,
          message: null,
          lastInboundAt: ok.lastInboundAt,
          windowExpiresAt: ok.windowExpiresAt,
        });
      } else if (res.status === 403) {
        const blocked = body as {
          error: string;
          reason: FreeFormBlockReason;
          lastInboundAt: string | null;
          windowExpiresAt: string | null;
        };
        setEligibility({
          loading: false,
          allowed: false,
          reason: blocked.reason,
          message: blocked.error,
          lastInboundAt: blocked.lastInboundAt,
          windowExpiresAt: blocked.windowExpiresAt,
        });
      } else {
        setEligibility({
          ...INITIAL_ELIGIBILITY,
          message: extractError(body) || `Eligibility check failed (HTTP ${res.status}).`,
        });
      }
      setStatus('idle');
    } catch (err) {
      if (reqId !== eligibilityReqId.current) return;
      setEligibility({
        ...INITIAL_ELIGIBILITY,
        message: err instanceof Error ? err.message : 'Eligibility check failed.',
      });
      setStatus('idle');
    }
  }, []);

  const send = useCallback(async (input: SendFreeFormInput): Promise<SendFreeFormResult | null> => {
    if (!FREEFORM_URL) {
      setError('Free-form WhatsApp endpoint is not configured for this client.');
      setStatus('error');
      return null;
    }
    setStatus('sending');
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(FREEFORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          patientId: input.patientId,
          message: input.message,
          sourceType: input.sourceType,
          appointmentId: input.appointmentId,
          clientReference: input.clientReference,
        }),
      });
      const body: unknown = await res.json().catch(() => null);

      if (!res.ok) {
        // 403 surfaces eligibility shape too — refresh the cached state.
        if (res.status === 403 && body && typeof body === 'object') {
          const blocked = body as {
            error?: string;
            reason?: FreeFormBlockReason;
            lastInboundAt?: string | null;
            windowExpiresAt?: string | null;
          };
          setEligibility({
            loading: false,
            allowed: false,
            reason: blocked.reason ?? null,
            message: blocked.error ?? null,
            lastInboundAt: blocked.lastInboundAt ?? null,
            windowExpiresAt: blocked.windowExpiresAt ?? null,
          });
        }
        setError(friendlyError(res.status, body));
        setStatus('error');
        return null;
      }

      const result = body as SendFreeFormResult;
      setLastResult(result);
      setStatus('success');
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send WhatsApp message.';
      setError(msg);
      setStatus('error');
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  // Reset transient state if the endpoint disappears (e.g. branding switch).
  useEffect(() => {
    if (!enabled) {
      setEligibility(INITIAL_ELIGIBILITY);
      setStatus('idle');
    }
  }, [enabled]);

  return {
    status,
    error,
    lastResult,
    enabled,
    eligibility,
    checkEligibility,
    send,
    reset,
  };
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function extractError(body: unknown): string {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    return String((body as { error?: unknown }).error ?? '');
  }
  return '';
}

function friendlyError(httpStatus: number, body: unknown): string {
  const raw = extractError(body);
  switch (httpStatus) {
    case 400:
      return raw || 'The message is invalid. Please review and try again.';
    case 401:
      return 'Your session expired. Please sign in again.';
    case 403:
      return raw || 'Free-form WhatsApp messaging is not currently allowed for this patient.';
    case 501:
      return raw || 'The configured WhatsApp provider does not support free-form sends.';
    default:
      return raw || `Failed to send WhatsApp message (HTTP ${httpStatus}).`;
  }
}
